import { css } from "@emotion/react"
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined"
import { LoadingMessage } from "@/components/LoadingMessage/LoadingMessage"
import { useTranslation } from "@/hooks/useTranslation"
import { useRankPreview } from "@/hooks/useRankPreview"

interface Props {
    workerId: string
    onClick: () => void
}

export const RankDashboardPreview = ({ workerId, onClick }: Props) => {
    const { t } = useTranslation()
    const { data, isLoading } = useRankPreview(workerId)

    if (isLoading) {
        return (
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    height: 80px;
                    margin: 16px 16px 0;
                    background: #f5f5f5;
                    border-radius: 12px;
                `}
            >
                <LoadingMessage />
            </div>
        )
    }

    if (!data) return null

    const stops = (Array.isArray(data.gradientStops) ? data.gradientStops : [])
        .filter(
            stop =>
                typeof stop?.color === "string" &&
                typeof stop?.offset === "number"
        )
        .map(stop => `${stop.color} ${stop.offset * 100}%`)

    // 有効な stop が無ければ gradientFrom/gradientTo にフォールバック
    const gradientStops =
        stops.length > 0
            ? stops.join(", ")
            : `${data.gradientFrom}, ${data.gradientTo}`

    // 非有限値なら従来どおり 135deg にフォールバック
    const gradientAngle = Number.isFinite(data.gradientAngle)
        ? data.gradientAngle
        : 135

    return (
        <div
            css={css`
                width: calc(100% - 32px);
                padding: 16px 20px;
                margin: 16px 16px 0;
                background: linear-gradient(
                    ${gradientAngle}deg,
                    ${gradientStops}
                );
                border-radius: 16px;
            `}
        >
            {/* ヘッダー行 */}
            <div
                css={css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 12px;
                    font-size: 14px;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.9);
                `}
            >
                <span>{data.rankLabel}</span>
                <EmojiEventsOutlinedIcon
                    css={css`
                        font-size: 22px;
                        color: rgba(255, 255, 255, 0.9);
                    `}
                />
            </div>

            {/* ランク情報行 */}
            <div
                css={css`
                    display: flex;
                    gap: 14px;
                    align-items: center;
                `}
            >
                <img
                    src={data.sealImageUrl}
                    alt={data.rank}
                    css={css`
                        width: 56px;
                        height: 56px;
                        object-fit: contain;
                    `}
                />
                <div>
                    <div
                        css={css`
                            font-family: "Inter", sans-serif;
                            font-size: 28px;
                            font-weight: 900;
                            line-height: 1.1;
                            color: white;
                            letter-spacing: 0.04em;
                        `}
                    >
                        {data.rank.toUpperCase()}
                    </div>
                    <div
                        css={css`
                            margin-top: 4px;
                            font-size: 13px;
                            font-weight: 600;
                            color: rgba(255, 255, 255, 0.9);
                        `}
                    >
                        {data.bonusText}
                    </div>
                    <div
                        css={css`
                            margin-top: 2px;
                            font-size: 13px;
                            font-weight: 600;
                            color: rgba(255, 255, 255, 0.75);
                        `}
                    >
                        {data.totalBonusText}
                    </div>
                </div>
            </div>

            {/* 詳細ボタン */}
            <button
                type="button"
                css={css`
                    display: block;
                    width: 100%;
                    padding: 8px 16px;
                    margin-top: 12px;
                    font-size: 13px;
                    font-weight: 600;
                    color: white;
                    text-align: center;
                    cursor: pointer;
                    background: rgba(255, 255, 255, 0.25);
                    border: 1px solid rgba(255, 255, 255, 0.5);
                    border-radius: 8px;
                `}
                onClick={onClick}
            >
                {t("スコア・報酬の詳細を見る")}
            </button>
        </div>
    )
}
